package com.evoapps.evoshift.navigation

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.displayCutout
import androidx.compose.foundation.layout.union
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBars
import androidx.compose.foundation.layout.systemBars
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AttachMoney
import androidx.compose.material.icons.filled.CalendarMonth
import androidx.compose.material.icons.filled.EditCalendar
import androidx.compose.material.icons.filled.EventAvailable
import androidx.compose.material.icons.filled.EventBusy
import androidx.compose.material.icons.filled.Home
import androidx.compose.material.icons.filled.Menu
import androidx.compose.material.icons.filled.People
import androidx.compose.material.icons.filled.RequestQuote
import androidx.compose.material3.Badge
import androidx.compose.material3.BadgedBox
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.NavigationBar
import androidx.compose.material3.NavigationBarItem
import androidx.compose.material3.NavigationBarItemDefaults
import androidx.compose.material3.NavigationRailItem
import androidx.compose.material3.NavigationRailItemDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.GoldMetallicBrush
import com.evoapps.evoshift.ui.adaptive.handCursor
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.app_logo
import evoshift.composeapp.generated.resources.nav_absences
import evoshift.composeapp.generated.resources.nav_availability_tab
import evoshift.composeapp.generated.resources.nav_home
import evoshift.composeapp.generated.resources.nav_money
import evoshift.composeapp.generated.resources.nav_more
import evoshift.composeapp.generated.resources.nav_my_shifts
import evoshift.composeapp.generated.resources.nav_requests
import evoshift.composeapp.generated.resources.nav_rosters
import evoshift.composeapp.generated.resources.nav_team
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

/** Test tag of a tab in the bar or the rail; `tab_<tab>`. */
fun AppTab.testTag(): String = "tab_${name.lowercase()}"

/** SH-164: the tab's count badge (unread notifications, requests to decide). */
fun AppTab.badgeTestTag(): String = "${testTag()}_badge"

fun AppTab.label(): StringResource = when (this) {
    AppTab.HOME -> Res.string.nav_home
    AppTab.MY_SHIFTS -> Res.string.nav_my_shifts
    AppTab.ROSTERS -> Res.string.nav_rosters
    AppTab.TEAM -> Res.string.nav_team
    AppTab.REQUESTS -> Res.string.nav_requests
    AppTab.MONEY -> Res.string.nav_money
    AppTab.AVAILABILITY -> Res.string.nav_availability_tab
    AppTab.ABSENCES -> Res.string.nav_absences
    AppTab.MORE -> Res.string.nav_more
}

fun AppTab.icon(): ImageVector = when (this) {
    AppTab.HOME -> Icons.Default.Home
    AppTab.MY_SHIFTS -> Icons.Default.CalendarMonth
    AppTab.ROSTERS -> Icons.Default.EditCalendar
    AppTab.TEAM -> Icons.Default.People
    AppTab.REQUESTS -> Icons.Default.RequestQuote
    AppTab.MONEY -> Icons.Default.AttachMoney
    AppTab.AVAILABILITY -> Icons.Default.EventAvailable
    AppTab.ABSENCES -> Icons.Default.EventBusy
    AppTab.MORE -> Icons.Default.Menu
}

@Composable
private fun TabIcon(tab: AppTab, badge: Int) {
    BadgedBox(badge = {
        if (badge > 0) Badge(containerColor = DarkGold, contentColor = DeepNavy, modifier = Modifier.testTag(tab.badgeTestTag())) {
            Text(if (badge > 9) "9+" else badge.toString())
        }
    }) {
        Icon(tab.icon(), contentDescription = null)
    }
}

/**
 * The phone's bottom bar: navy with gold for the selected tab, one-line labels (as EVoLabels'
 * bar: a fixed small size that never wraps).
 */
@Composable
fun AppNavigationBar(
    tabs: List<AppTab>,
    selected: AppTab,
    badges: Map<AppTab, Int>,
    onSelect: (AppTab) -> Unit,
    modifier: Modifier = Modifier
) {
    NavigationBar(modifier = modifier, containerColor = DeepNavy, contentColor = DarkGold, tonalElevation = 0.dp) {
        tabs.forEach { tab ->
            NavigationBarItem(
                selected = tab == selected,
                onClick = { onSelect(tab) },
                icon = { TabIcon(tab, badges[tab] ?: 0) },
                label = {
                    Text(
                        stringResource(tab.label()),
                        fontSize = 11.sp,
                        maxLines = 1,
                        softWrap = false,
                        overflow = TextOverflow.Ellipsis
                    )
                },
                colors = NavigationBarItemDefaults.colors(
                    selectedIconColor = DarkGold,
                    selectedTextColor = DarkGold,
                    indicatorColor = DarkGold.copy(alpha = 0.18f),
                    unselectedIconColor = Color.White.copy(alpha = 0.6f),
                    unselectedTextColor = Color.White.copy(alpha = 0.6f)
                ),
                modifier = Modifier.handCursor().testTag(tab.testTag())
            )
        }
    }
}

/**
 * The system areas the rail's navy covers and keeps its tabs out of (SH-160): the side navigation
 * bar and the bottom (home indicator, gesture bar), and in landscape the camera's side, which is a
 * display cutout on Android and on iPhone (where `systemBars` also carries it). The shell consumes
 * the start side for the screens beside the rail, so they do not pad it again.
 */
val RailWindowInsets: WindowInsets
    @Composable get() = WindowInsets.systemBars.union(WindowInsets.displayCutout)
        .only(WindowInsetsSides.Bottom + WindowInsetsSides.Start)

/** Test tag of the whole rail, its navy under the system areas included. */
const val RAIL_TEST_TAG = "app_navigation_rail"

/** Below this height (a phone in landscape) the rail drops the logo and tightens its items (SH-83). */
internal val ShortRailBelow = 560.dp

/**
 * The wide screens' rail (tablets, browsers): the same tabs under the brand (the workplace is the
 * top bar's, SH-89). 96dp: long Polish labels overflow the default 80dp.
 *
 * It scrolls when the tabs do not fit (SH-83: a phone in landscape squeezed the items onto each
 * other, the selected pill covered the next label), and on a short window it leaves out the logo.
 * [statusBarColor] paints the strip under the status bar, so the whole status bar sits on one
 * colour and its icons (light or dark, [StatusBarIcons]) stay readable.
 */
@Composable
fun AppNavigationRail(
    tabs: List<AppTab>,
    selected: AppTab,
    badges: Map<AppTab, Int>,
    onSelect: (AppTab) -> Unit,
    modifier: Modifier = Modifier,
    statusBarColor: Color = DeepNavy
) {
    BoxWithConstraints(
        modifier
            .testTag(RAIL_TEST_TAG)
            .fillMaxHeight()
            .background(statusBarColor)
            .windowInsetsPadding(WindowInsets.statusBars.only(WindowInsetsSides.Top))
            .background(DeepNavy)
            .windowInsetsPadding(RailWindowInsets)
            // SH-160: the tabs' 96dp come after the inset: the navy grows under the notch / side
            // navigation bar instead of the inset squeezing the tabs (an iPhone in landscape lost
            // 62 of its 96dp and the screen's background covered the selected pill).
            .width(96.dp)
    ) {
        val short = maxHeight < ShortRailBelow
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .selectableGroup()
                .padding(vertical = if (short) 8.dp else 4.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            if (!short) {
                Image(
                    painter = painterResource(Res.drawable.app_logo),
                    contentDescription = "EVoShift",
                    modifier = Modifier.padding(top = 12.dp, bottom = 12.dp).size(52.dp)
                )
            }
            RailTabs(tabs, selected, badges, onSelect)
        }
    }
}

@Composable
private fun RailTabs(tabs: List<AppTab>, selected: AppTab, badges: Map<AppTab, Int>, onSelect: (AppTab) -> Unit) {
        tabs.forEach { tab ->
            NavigationRailItem(
                selected = tab == selected,
                onClick = { onSelect(tab) },
                icon = { TabIcon(tab, badges[tab] ?: 0) },
                label = {
                    Text(
                        stringResource(tab.label()),
                        style = MaterialTheme.typography.labelSmall,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                },
                colors = NavigationRailItemDefaults.colors(
                    selectedIconColor = DeepNavy,
                    selectedTextColor = DarkGold,
                    indicatorColor = DarkGold,
                    unselectedIconColor = Color.White.copy(alpha = 0.6f),
                    unselectedTextColor = Color.White.copy(alpha = 0.6f)
                ),
                modifier = Modifier.handCursor().testTag(tab.testTag())
            )
        }
}
